import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import buildingImage from '../assets/building.png';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <>
      <SEO 
        title="Luxury Real Estate Developers | HBB0011 Realty" 
        description="HBB0011 Realty offers premium residential and commercial projects. Experience visionary architectural design, sustainable living, and ultra-luxury real estate solutions."
        keywords="luxury real estate, corporate architecture, exclusive properties, real estate investments, HBB0011 REALTY"
        isHome={true}
      />
      {/* Side NavBar (Fixed Floating Right - Hidden on mobile/tablet) */}
      <aside className="hidden xl:flex fixed right-8 top-1/2 -translate-y-1/2 rounded-xl bg-surface-container/40 backdrop-blur-md border border-outline-variant/30 shadow-xl z-40 flex-col items-center py-8 gap-6 w-20">
        <Link to="/projects" className="group relative flex justify-center w-full py-4 text-on-surface-variant hover:bg-surface-container-highest transition-all duration-300 cursor-pointer">
          <span className="material-symbols-outlined group-hover:text-secondary transition-colors">apartment</span>
          <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-surface-container border border-outline-variant px-4 py-2 rounded font-label-caps text-[10px] text-on-surface opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-lg">RESIDENTIAL</div>
        </Link>
        <Link to="/projects" className="group relative flex justify-center w-full py-4 text-on-surface-variant hover:bg-surface-container-highest transition-all duration-300 cursor-pointer">
          <span className="material-symbols-outlined group-hover:text-secondary transition-colors">business_center</span>
          <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-surface-container border border-outline-variant px-4 py-2 rounded font-label-caps text-[10px] text-on-surface opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-lg">COMMERCIAL</div>
        </Link>
        <Link to="/investors" className="group relative flex justify-center w-full py-4 text-on-surface-variant hover:bg-surface-container-highest transition-all duration-300 cursor-pointer">
          <span className="material-symbols-outlined group-hover:text-secondary transition-colors">payments</span>
          <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-surface-container border border-outline-variant px-4 py-2 rounded font-label-caps text-[10px] text-on-surface opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-lg">INVESTMENTS</div>
        </Link>
        <Link to="/contact" className="group relative flex justify-center w-full py-4 text-on-surface-variant hover:bg-surface-container-highest transition-all duration-300 cursor-pointer mt-4 border-t border-outline-variant">
          <span className="material-symbols-outlined text-secondary">headset_mic</span>
          <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-secondary text-on-secondary px-4 py-2 rounded font-label-caps text-[10px] font-bold opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap shadow-lg">ENQUIRE NOW</div>
        </Link>
      </aside>

      {/* Hero Section */}
      <header className="relative min-h-[90vh] lg:min-h-screen flex items-center justify-start pt-24 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 w-full h-full z-0">
          <img 
            alt="Hero architectural building at dusk" 
            className="w-full h-full object-cover object-center" 
            src={buildingImage} 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-container/90 via-primary-container/60 to-transparent"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-container-max mx-auto px-6 md:px-margin-desktop pt-10 pb-32">
          <Reveal>
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="material-symbols-outlined text-secondary text-[16px]">diamond</span>
                <span className="font-label-caps text-label-caps text-secondary tracking-[0.3em]">A LANDMARK THAT DEFINES</span>
              </div>
              <h1 className="font-display-lg text-[40px] sm:text-[60px] md:text-display-lg text-on-surface mb-8 leading-tight">
                  VISION.<br />TRUST.<br /><span className="text-secondary font-medium">EXCELLENCE.</span>
              </h1>
            </div>
          </Reveal>
          
          <Reveal delay={0.2}>
            <p className="font-body-md text-body-md text-on-surface-variant mb-12 max-w-xl">
                HBB0011 Realty Realtors Pvt. Ltd. is committed to creating iconic spaces that blend architectural brilliance with sustainable living, setting new benchmarks in the ultra-luxury real estate sector.
            </p>
          </Reveal>
          
          <Reveal delay={0.4}>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <Link to="/projects" className="btn-primary font-label-caps text-label-caps px-6 sm:px-8 py-4 flex items-center gap-2 group text-sm sm:text-base">
                  EXPLORE PROJECTS
                  <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </Link>
              <Link to="/contact" className="font-label-caps text-label-caps text-on-surface border border-outline-variant px-6 sm:px-8 py-4 flex items-center gap-2 hover:border-secondary hover:text-secondary transition-colors group bg-transparent text-sm sm:text-base">
                  GET IN TOUCH
                  <span className="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </Link>
            </div>
          </Reveal>
        </div>
        
        {/* Floating Statistics Bar - Grid adapts by device */}
        <div className="absolute bottom-0 left-0 w-full glass-panel z-20 border-t border-b-0 border-l-0 border-r-0">
          <div className="max-w-container-max mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-y-6 gap-x-4">
              <div className="flex flex-col items-center justify-center text-center">
                <span className="material-symbols-outlined text-secondary text-[24px] md:text-[28px] mb-1 md:mb-2 font-light">schedule</span>
                <span className="font-headline-lg text-[20px] md:text-[28px] text-secondary font-light mb-0 md:mb-1">1+</span>
                <span className="font-label-caps text-[8px] md:text-[10px] text-on-surface-variant tracking-[0.2em]">YEARS</span>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <span className="material-symbols-outlined text-secondary text-[24px] md:text-[28px] mb-1 md:mb-2 font-light">foundation</span>
                <span className="font-headline-lg text-[20px] md:text-[28px] text-secondary font-light mb-0 md:mb-1">1+</span>
                <span className="font-label-caps text-[8px] md:text-[10px] text-on-surface-variant tracking-[0.2em]">PROJECTS</span>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <span className="material-symbols-outlined text-secondary text-[24px] md:text-[28px] mb-1 md:mb-2 font-light">groups</span>
                <span className="font-headline-lg text-[20px] md:text-[28px] text-secondary font-light mb-0 md:mb-1">10+</span>
                <span className="font-label-caps text-[8px] md:text-[10px] text-on-surface-variant tracking-[0.2em]">CUSTOMERS</span>
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <span className="material-symbols-outlined text-secondary text-[24px] md:text-[28px] mb-1 md:mb-2 font-light">location_on</span>
                <span className="font-headline-lg text-[20px] md:text-[28px] text-secondary font-light mb-0 md:mb-1">5+</span>
                <span className="font-label-caps text-[8px] md:text-[10px] text-on-surface-variant tracking-[0.2em]">LOCATIONS</span>
              </div>
              <div className="flex flex-col items-center justify-center text-center col-span-2 sm:col-span-1 lg:col-span-1 border-t sm:border-t-0 border-outline-variant/20 pt-4 sm:pt-0">
                <span className="material-symbols-outlined text-secondary text-[24px] md:text-[28px] mb-1 md:mb-2 font-light">verified</span>
                <span className="font-label-caps text-[8px] md:text-[10px] text-on-surface-variant tracking-[0.2em]">QUALITY TRUST</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* About Us & Projects Grid Section */}
      <section className="py-20 md:py-32 px-6 md:px-margin-desktop relative" id="about">
        <div className="max-w-container-max mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            
            {/* Left: About Content */}
            <div className="lg:col-span-4 flex flex-col justify-center">
              <Reveal>
                <h3 className="font-label-caps text-label-caps text-secondary tracking-[0.3em] mb-6">ABOUT US</h3>
                <h2 className="font-headline-lg text-[32px] md:text-[42px] leading-tight text-on-surface font-light mb-8">
                    BUILDING <span className="font-medium">TRUST.</span><br />
                    CREATING <span className="font-medium">LANDMARKS.</span>
                </h2>
                <div className="w-12 h-px bg-secondary mb-8"></div>
                <p className="font-body-md text-body-md text-on-surface-variant mb-6">
                    At HBB0011 Realty, we don't just construct buildings; we engineer lifestyles. Our architectural philosophy centers on minimal intervention with maximum impact, ensuring every development harmonizes with its environment while providing unparalleled luxury.
                </p>
                <p className="font-body-md text-body-md text-on-surface-variant mb-10">
                    With a decade of unwavering commitment to quality and transparency, our portfolio stands as a testament to visionary design and meticulous execution in the ultra-luxury segment.
                </p>
                <Link to="/about" className="group flex items-center gap-3 w-max">
                  <span className="font-label-caps text-label-caps text-secondary">READ OUR STORY</span>
                  <div className="w-8 h-px bg-secondary group-hover:w-16 transition-all duration-300"></div>
                </Link>
              </Reveal>
            </div>
            
            {/* Right: Projects Bento Grid */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                
                {/* Grid Item 1 */}
                <Reveal delay={0.2} width="100%">
                  <Link to="/projects" className="group relative overflow-hidden h-[300px] md:h-[400px] border border-outline-variant/30 flex items-end block w-full">
                    <div className="absolute inset-0 bg-surface-container-low">
                      <img 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuBEuuR7QoSKhY4PN3inji1UL8fd7GruMAbnTpxxEB2FUA30oocwBRKC9FTImv9s_u7UKo7ZgR8-3pK4a7PrcD_g0Rhh6dOL_zcNEHaoRUnawQ-EquAJNEck_N77ECGy65btqoOhh50jFaY2UzN3ccSUQEjqpwHHLBVbFyGBeJ9tLYO7aq3k6SoO7oF2jy7kzH91Jn5bNIC6hz6xd7r1w1d1XJFDDDqFf3lMFCJkyv4ePUiaxiiR0OAL3pH60VgIGVtyVayQMPbhyPU" 
                        alt="Residential Projects"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                    <div className="relative z-10 p-6 md:p-8 w-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <span className="material-symbols-outlined text-secondary mb-3">apartment</span>
                      <h3 className="font-label-caps text-[12px] md:text-[14px] text-on-surface tracking-widest mb-2 flex items-center justify-between">
                          RESIDENTIAL PROJECTS
                          <span className="material-symbols-outlined text-[18px] opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all duration-300">arrow_forward</span>
                      </h3>
                      <div className="w-0 h-px bg-secondary group-hover:w-full transition-all duration-500 ease-in-out mt-4"></div>
                    </div>
                  </Link>
                </Reveal>
                
                {/* Grid Item 2 */}
                <Reveal delay={0.4} width="100%">
                  <Link to="/projects" className="group relative overflow-hidden h-[300px] md:h-[400px] border border-outline-variant/30 flex items-end block w-full">
                    <div className="absolute inset-0 bg-surface-container-low">
                      <img 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuByd3nHdLV07J9eH6mZfaAVbqz-GnbyTt5uMyEgcyUPXXYPADaJXi2GpllcAaeH4HCvxYKcCXSYlw4GpMkoWKfwq3LP3agggmnPJZqhlVNBrRKwUzg2BB4CZV8sIBZPNJS6ybzKuueF_a-s7osVcUQWqNM6K81lfXoxYsAjDhmpg6RFlybktq5ONlBzMUbwb9uAPwMoe3I4m7l9fN8_Xem064qgzcotigqFd0OWvt4apKxgsnpdpSakhmhf-xuTbNe3QUKtTiXCLtI" 
                        alt="Commercial Spaces"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                    <div className="relative z-10 p-6 md:p-8 w-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <span className="material-symbols-outlined text-secondary mb-3">business_center</span>
                      <h3 className="font-label-caps text-[12px] md:text-[14px] text-on-surface tracking-widest mb-2 flex items-center justify-between">
                          COMMERCIAL SPACES
                          <span className="material-symbols-outlined text-[18px] opacity-0 group-hover:opacity-100 -translate-x-4 group-hover:translate-x-0 transition-all duration-300">arrow_forward</span>
                      </h3>
                      <div className="w-0 h-px bg-secondary group-hover:w-full transition-all duration-500 ease-in-out mt-4"></div>
                    </div>
                  </Link>
                </Reveal>
                
              </div>
            </div>
            
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;